import { createMemo, createResource, onCleanup, type JSX, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { useLocation } from "@solidjs/router"
import { createSimpleContext } from "@opencode-ai/ui/context"
import { useLanguage } from "@/context/language"
import { datoxAgentMode, getCurrentUser, listCompanies, setDatoxAuthTokenProvider } from "@/datox/api"
import { activeCompanyID, setActiveCompanyID } from "@/datox/local-auth"
import { loginErrorKey } from "@/datox/login-error"
import { DatoxContentLoading } from "@/components/datox-content-loading"
export { activeCompanyID, localDevHeaders, setActiveCompanyID } from "@/datox/local-auth"

type DatoxAuthState = {
  initialized: boolean
  accountName: string
  accountEmail: string
  accessToken: string
  error: ReturnType<typeof loginErrorKey> | ""
  signingIn: boolean
}

const defaultTenantID = "95c228f4-944c-49ba-abcd-a00985e5497c"
const defaultScope = "api://d76b434a-b9ac-4bb1-b417-40d2eb65d624/user_impersonation"

export const { provider: DatoxAuthProvider, use: useDatoxAuth } = createSimpleContext({
  name: "DatoxAuth",
  gate: false,
  init: () => {
    const [state, setState] = createStore<DatoxAuthState>({
      initialized: false,
      accountName: "",
      accountEmail: "",
      accessToken: "",
      error: "",
      signingIn: false,
    })

    const [client, clientActions] = createResource(async () => {
      if (!isMsalConfigured()) {
        setState("initialized", true)
        setState("error", msalRequired() ? "datox.login.error.configuration" : "")
        return null
      }
      try {
        const msal = await import("@azure/msal-browser")
        const instance = new msal.PublicClientApplication({
          auth: {
            clientId: import.meta.env.VITE_DATOX_MSAL_CLIENT_ID || "",
            authority: `https://login.microsoftonline.com/${import.meta.env.VITE_DATOX_MSAL_TENANT_ID || defaultTenantID}`,
            redirectUri: import.meta.env.VITE_DATOX_MSAL_REDIRECT_URI || window.location.origin,
          },
          cache: {
            // Report previews open in independent tabs. MSAL's durable cache
            // must be shared; redirect/interaction state remains tab-local.
            cacheLocation: "localStorage",
            storeAuthStateInCookie: false,
          },
        })
        await instance.initialize()
        const redirect = await instance.handleRedirectPromise()
        if (redirect?.account) instance.setActiveAccount(redirect.account)
        if (!instance.getActiveAccount() && instance.getAllAccounts()[0]) instance.setActiveAccount(instance.getAllAccounts()[0])
        setState("initialized", true)
        await acquireToken(instance)
        return instance
      } catch (error) {
        setState("initialized", true)
        setState("error", loginErrorKey(error))
        return null
      }
    })

    const login = async () => {
      if (state.signingIn || client.loading || !isMsalConfigured()) return
      setState({ signingIn: true, error: "" })
      try {
        const instance = client() || await clientActions.refetch()
        if (!instance) return
        await instance.loginRedirect({ scopes: [scope()], redirectUri: redirectUri(), state: window.location.href })
      } catch (error) {
        setState("error", loginErrorKey(error))
      } finally {
        setState("signingIn", false)
      }
    }

    const logout = async () => {
      const instance = client()
      if (!instance) return
      await instance.logoutRedirect()
    }

    const token = async (forceRefresh = false) => {
      const instance = client()
      if (!instance) return ""
      return acquireToken(instance, forceRefresh)
    }

    const configured = createMemo(isMsalConfigured)
    const authenticated = createMemo(() => !msalRequired() || !!state.accessToken)

    return {
      state,
      configured,
      authenticated,
      initializing: () => client.loading,
      login,
      logout,
      token,
    }

    async function acquireToken(instance: import("@azure/msal-browser").PublicClientApplication, forceRefresh = false) {
      const account = instance.getActiveAccount() || instance.getAllAccounts()[0]
      if (!account) {
        setState({ initialized: true, accountName: "", accountEmail: "", accessToken: "" })
        return ""
      }
      try {
        const response = await instance.acquireTokenSilent({ scopes: [scope()], account, forceRefresh })
        setState({
          initialized: true,
          accountName: response.account?.name || account.name || "",
          accountEmail: response.account?.username || account.username || "",
          accessToken: response.accessToken,
          error: "",
        })
        return response.accessToken
      } catch (error) {
        setState({ accessToken: "", error: loginErrorKey(error) })
        return ""
      }
    }
  },
})

export function DatoxAuthGate(props: { children: JSX.Element }) {
  const auth = useDatoxAuth()
  const language = useLanguage()
  const location = useLocation()

  setDatoxAuthTokenProvider(auth.token)
  const scoped = () => datoxAgentMode() && msalRequired() && !location.pathname.startsWith("/review/")
  const [companyAccess, companyActions] = createResource(
    () => (scoped() && auth.authenticated() ? auth.state.accountEmail || true : undefined),
    () => listCompanies().then(async (available) => {
      if (available.length) {
        if (!available.some((company) => company.id === activeCompanyID())) setActiveCompanyID(available[0].id)
        return "ready"
      }
      return getCurrentUser().then((user) => user.is_datox_admin ? "ready" : "denied").catch(() => "denied")
    }).catch(() => "error"),
  )
  const syncCompany = (event: StorageEvent) => {
    if (event.key === "activeCompanyId" && event.oldValue !== event.newValue) window.location.assign("/")
  }
  window.addEventListener("storage", syncCompany)
  onCleanup(() => window.removeEventListener("storage", syncCompany))

  if (!datoxAgentMode() || location.pathname.startsWith("/review/")) return props.children

  return (
    <Show when={!auth.initializing()} fallback={<DatoxContentLoading title={language.t("datox.login.loading")} />}>
      <Show when={auth.authenticated()} fallback={<DatoxLoginPage />}>
        <Show when={!scoped() || !companyAccess.loading} fallback={<DatoxContentLoading title={language.t("datox.login.loading")} />}>
          <Show when={!scoped() || companyAccess() === "ready"} fallback={
            <div class="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-v2-text-text-base">
              <p role="alert">{language.t(companyAccess() === "error" ? "datox.admin.loadFailed" : "datox.company.noAccess")}</p>
              <button type="button" onClick={() => void companyActions.refetch()}>{language.t("datox.members.retry")}</button>
              <button type="button" onClick={() => void auth.logout()}>{language.t("datox.profile.logout")}</button>
            </div>
          }>
            {props.children}
          </Show>
        </Show>
      </Show>
    </Show>
  )

  function DatoxLoginPage() {
    return (
      <div class="relative flex min-h-dvh items-center justify-center overflow-hidden bg-[#06131a] px-6 py-8 text-[#ffffff]">
        <div class="pointer-events-none absolute -left-24 top-10 h-80 w-80 rounded-full bg-[#0085ff]/20 blur-3xl" />
        <div class="pointer-events-none absolute -right-24 bottom-0 h-96 w-96 rounded-full bg-[#34d399]/10 blur-3xl" />
        <div class="relative flex w-full max-w-xl flex-col items-center rounded-[28px] border border-white/10 bg-white/[0.04] px-8 py-12 text-center shadow-2xl backdrop-blur md:px-14 md:py-16">
          <img src="/datox-logo-white.svg" alt={language.t("datox.logo.alt")} class="h-16 w-auto md:h-20" />
          <div class="mt-16 inline-flex rounded-full border border-[#0085ff]/35 bg-[#0085ff]/10 px-5 py-2 text-18-medium text-[#7cc4ff]">
            {language.t("datox.login.badge")}
          </div>
          <Show when={auth.state.error}>
            {(error) => <div role="alert" aria-atomic="true" class="mt-8 flex w-full gap-3 rounded-xl border border-[#fcd34d40] bg-[#fbbf2414] p-4 text-left text-13-regular text-[#fffbeb]">
              <span aria-hidden="true" class="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border border-[#fde68a99] font-semibold text-[#fde68a]">!</span>
              <div>
                <h2 class="font-semibold text-[#fffbeb]">{language.t("datox.login.error.title")}</h2>
                <p id="datox-login-error" class="mt-1 leading-relaxed text-[#e8dfc5]">{language.t(error())}</p>
              </div>
            </div>}
          </Show>
          <button
            class="mt-8 flex h-12 w-full max-w-sm items-center justify-center rounded-xl bg-[#0085ff] px-4 text-14-medium text-[#ffffff] shadow-lg shadow-[#0085ff]/25 transition hover:bg-[#1d93ff] disabled:cursor-not-allowed disabled:opacity-50"
            disabled={auth.initializing() || auth.state.signingIn || !auth.configured()}
            aria-busy={auth.state.signingIn}
            aria-describedby={auth.state.error ? "datox-login-error" : undefined}
            onClick={() => void auth.login()}
          >
            {language.t(auth.state.signingIn ? "datox.login.signingIn" : auth.state.error && auth.configured() ? "datox.login.retry" : "datox.login.button")}
          </button>
        </div>
      </div>
    )
  }
}

function isMsalConfigured() {
  return !!import.meta.env.VITE_DATOX_MSAL_CLIENT_ID
}

function redirectUri() {
  return import.meta.env.VITE_DATOX_MSAL_REDIRECT_URI || window.location.origin
}

function msalRequired() {
  if (!datoxAgentMode()) return false
  if (isMsalConfigured()) return true
  try {
    const url = new URL(import.meta.env.VITE_DATOX_AGENT_API_URL || "http://localhost:8088")
    return url.hostname !== "localhost" && url.hostname !== "127.0.0.1"
  } catch {
    return true
  }
}

function scope() {
  return import.meta.env.VITE_DATOX_MSAL_SCOPE || defaultScope
}
